import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Auth } from "aws-amplify";
import axios from "axios";
import { API_URL } from "../consts";
import { signOut } from "../utils/authUtils";

// MUI Components
import {
  AppBar,
  Toolbar,
  Box,
  Avatar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Drawer,
  Badge,
  Tooltip,
  Divider,
  useMediaQuery,
  useTheme,
  CssBaseline,
  ListItemIcon,
  ListItemText,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Input,
  InputAdornment,
} from "@mui/material";

// MUI Icons
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Login as LoginIcon,
  CloudDownload as CloudDownloadIcon,
  Stars as StarsIcon,
  Dashboard as DashboardIcon,
  Mail as MailIcon,
  Search as SearchIcon,
  MenuOpen as MenuOpenIcon,
  Support as SupportIcon,
  Business as BusinessIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";

// Local Components
import Sidebar from "./Sidebar/Sidebar";
import MiniSidebar from "./Sidebar/MiniSidebar";
import ChipList from "./common/ChipList";

// Styles
import "./Header.css";

const drawerWidth = 280;

export default function Header({ setProfile, sidebarOpen, setSidebarOpen, isVisible = true }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));

  // Redux State
  const userFromStore = useSelector((state) => state.user);
  const authorized = useSelector((state) => state.authorized);
  const searchState = useSelector((state) => state.search);

  // Local State
  const [user, setUser] = useState(userFromStore);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifAnchorEl, setNotifAnchorEl] = useState(null);
  const [searchQuery, setSearchQuery] = useState(searchState.query);
  const [isVApplicationWrapPresent, setIsVApplicationWrapPresent] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [isExtensionInstalled, setIsExtensionInstalled] = useState(true); // Default to true to avoid flash
  const [mobileHeaderVisible, setMobileHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  // Replace mock notifications with API state
  const [notifications, setNotifications] = useState([]);
  const [newNotifications, setNewNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch notifications
  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/notifications`);
      setNotifications(response.data.notifications);
      setNewNotifications(response.data.new_notifications);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch notifications on component mount
  useEffect(() => {
    if (authorized) {
      fetchNotifications();
    }
  }, [authorized]);

  // Calculate unread notifications (only from new notifications)
  const unreadCount = newNotifications.length;

  // Menu states
  const open = Boolean(anchorEl);
  const openNotif = Boolean(notifAnchorEl);

  // Update user when store changes
  useEffect(() => {
    setUser(userFromStore);
  }, [userFromStore]);

  // Update search query when Redux state changes
  useEffect(() => {
    setSearchQuery(searchState.query);
  }, [searchState.query]);

  // Detect if Datarate extension is installed
  useEffect(() => {
    let timeoutId;
    let messageHandler;

    // Function to detect extension
    const detectExtension = () => {
      // Set a timeout - if no response in 500ms, assume extension is not installed
      timeoutId = setTimeout(() => {
        setIsExtensionInstalled(false);
      }, 500);

      // Listen for response from extension
      messageHandler = (event) => {
        // Check if this is a response from the extension
        if (event.data && event.data.type === 'extension-detected') {
          clearTimeout(timeoutId);
          setIsExtensionInstalled(true);
        }
      };

      window.addEventListener('message', messageHandler);

      // Try to ping the extension - using window.location.origin for security
      window.postMessage(
        {
          action: 'ping',
          source: 'datarate-web',
          timestamp: Date.now()
        },
        window.location.origin
      );
    };

    detectExtension();

    // Cleanup
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (messageHandler) {
        window.removeEventListener('message', messageHandler);
      }
    };
  }, []);

  // Handle scroll detection for mobile header hide/show - only on feed view pages
  useEffect(() => {
    // Only apply scroll behavior on mobile and on feed view pages
    const feedViewPages = ['/community', '/my-reviews', '/community-spaces'];
    const isFeedViewPage = feedViewPages.includes(location.pathname);
    
    if (!isMobile || !isFeedViewPage) {
      setMobileHeaderVisible(true);
      setLastScrollY(0); // Reset scroll position when leaving feed view pages
      return;
    }

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollThreshold = 10; // Minimum scroll distance to trigger hide/show

      // Show header when at top of page
      if (currentScrollY < scrollThreshold) {
        setMobileHeaderVisible(true);
      } 
      // Hide header when scrolling down
      else if (currentScrollY > lastScrollY && currentScrollY > scrollThreshold) {
        setMobileHeaderVisible(false);
      } 
      // Show header when scrolling up
      else if (currentScrollY < lastScrollY) {
        setMobileHeaderVisible(true);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isMobile, location.pathname, lastScrollY]);

  // Handle drawer toggle - simple and direct
  const handleDrawerToggle = () => {
    if (setSidebarOpen) {
      setSidebarOpen(!sidebarOpen);
    }
  };

  // Handle closing sidebar on navigation
  const componentChange = () => {
    setMobileOpen(false);
  };

  // Handle profile menu
  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  // Handle notifications
  const handleNotifMenuOpen = (event) => {
    setNotifAnchorEl(event.currentTarget);
  };

  // Handle marking notifications as read
  const handleNotifMenuClose = async () => {
    try {
      if (newNotifications.length > 0) {
        // Mark new notifications as read
        await axios.post(`${API_URL}/notifications/read-all`, {
          notification_ids: newNotifications.map(notif => notif.id)
        });
        // Update local state
        setNewNotifications([]);
      }
    } catch (error) {
      console.error("Error marking notifications as read:", error);
    }
    setNotifAnchorEl(null);
  };

  // Format date helper function
  const formatNotificationDate = (dateString) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInHours = Math.abs(now - date) / 36e5;
      
      if (diffInHours < 24) {
        return `${Math.round(diffInHours)}h ago`;
      } else if (diffInHours < 48) {
        return 'Yesterday';
      } else {
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Invalid date";
    }
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      setAnchorEl(null);
      // Use the signOut utility that clears all Cognito cookies and redirects to Google logout
      // Pass true to redirect to login page after logout
      await signOut(true);
      dispatch({ type: "SET_UNAUTHORIZED" });
      // Note: signOut(true) will handle the redirect, so we don't need to navigate here
    } catch (error) {
      console.error("Error signing out: ", error);
      // Even if there's an error, still dispatch unauthorized and navigate
      dispatch({ type: "SET_UNAUTHORIZED" });
      navigate("/login");
    }
  };
  // Determine if upgrade button should be shown
  const showUpgrade = (user && user.plan_name === "Free") || (user && !user.plan_name);

  const fullName = user?.first_name ? `${user.first_name} ${user.last_name}` : "Guest";
  const userInitials = user?.first_name ? `${user.first_name.charAt(0)}${user.last_name?.charAt(0) || ''}` : "G";

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    dispatch({
      type: "UPDATE_SEARCH",
      payload: { 
        query: value,
        // Don't update tags here, only on Enter press
      }
    });
  };

  // Handle search submit
  const handleSearchSubmit = (e) => {
    if (e.key === "Enter") {
      const newTag = searchQuery.trim();
      if (newTag) {
        // Add the new tag to existing tags
        const updatedTags = [...searchState.tags, newTag];
        dispatch({
          type: "UPDATE_SEARCH",
          payload: { 
            query: "", // Clear the input
            tags: updatedTags
          }
        });
        setSearchQuery(""); // Clear the input field
        
        // Trigger search based on current route
        const path = location.pathname;
        if (path.includes("/my-reviews")) {
          dispatch({ type: "GET_RATES", payload: true });
        } else if (path.includes("/community")) {
          dispatch({ type: "GET_RATES", payload: true });
        }
        
        // Close mobile search on submit
        setMobileSearchOpen(false);
      }
    }
  };

  // "Exact search" only changes the result set on the pages that send the flag
  // to the API (the review lists), so hide it everywhere else.
  const showExactSearch = location.pathname.includes("my-reviews");

  const handleExactSearchChange = (e) => {
    dispatch({
      type: "UPDATE_SEARCH",
      payload: { exactSearch: e.target.checked }
    });
  };

  const exactSearchToggle = (
    <Tooltip title="Match the typed words exactly instead of AI semantic search" arrow>
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={!!searchState.exactSearch}
            onChange={handleExactSearchChange}
            sx={{ p: 0.5 }}
          />
        }
        label="Exact search"
        sx={{
          flexShrink: 0,
          ml: 0.5,
          mr: 0,
          whiteSpace: 'nowrap',
          '& .MuiFormControlLabel-label': {
            fontSize: '12px',
            color: 'rgba(71,85,105,0.9)'
          }
        }}
      />
    </Tooltip>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          background: "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.95) 100%)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          color: "#1a202c",
          borderBottom: "1px solid rgba(226,232,240,0.6)",
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
          transform: isMobile 
            ? (mobileHeaderVisible ? 'translateY(0)' : 'translateY(-100%)')
            : (isVisible ? 'translateY(0)' : 'translateY(-100%)'),
          transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'transform',
        }}
      >
        <Toolbar sx={{ 
          justifyContent: "space-between", 
          height: 36,
          px: { xs: 1.5, sm: 2 },
          background: "transparent"
        }}>
          {/* Left section: Menu button (desktop) and Logo */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
                color="inherit"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ 
                  background: "rgba(255,255,255,0.8)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(226,232,240,0.5)",
                  borderRadius: "8px",
                  width: 32,
                  height: 32,
                  display: { xs: "none", sm: "flex" }, // Hide on mobile (xs), show on sm and up
                  alignItems: "center",
                  justifyContent: "center",
                  "&:hover": { 
                    background: "rgba(255,255,255,0.9)",
                    transform: "translateY(-1px)",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
                  },
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
                }}
              >
                {sidebarOpen ? 
                  <MenuOpenIcon sx={{ fontSize: 20 }} /> : 
                  <MenuIcon sx={{ fontSize: 20 }} />
                }
              </IconButton>

            <img
              src={`${process.env.PUBLIC_URL}/Datarate_logo_new.svg`}
              alt="Datarate"
              onClick={() => navigate("/")}
              style={{ 
                paddingLeft:'20px',
                height: 52,
                width: "auto",
                cursor: "pointer",
                transition: "transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                display: "block",
                background: "transparent",
                border: "none",
                outline: "none",
                boxShadow: "none",
                margin: 0,
                padding: 0
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = "scale(1.02)";
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = "scale(1)";
              }}
            />
          </Box>

          {/* Center section: Search */}
          <Box sx={{ 
            display: "flex", 
            alignItems: "center", 
            flex: 1, 
            maxWidth: 500,
            mx: 2,
            display: { xs: 'none', sm: 'flex' }
          }}>
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                background: "rgba(255,255,255,0.9)",
                backdropFilter: "blur(20px)",
                borderRadius: "10px",
                border: "1px solid rgba(226,232,240,0.6)",
                boxShadow: "0 1px 4px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.1)",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                minHeight: 32,
                py: searchState.tags.length > 0 ? 0.5 : 0.25,
                px: 1,
                "&:hover": {
                  border: "1px solid rgba(99,102,241,0.3)",
                  boxShadow: "0 2px 8px rgba(99,102,241,0.1), 0 1px 4px rgba(0,0,0,0.05)",
                  transform: "translateY(-1px)"
                },
                "&:focus-within": {
                  border: "1px solid rgba(99,102,241,0.5)",
                  boxShadow: "0 0 0 2px rgba(99,102,241,0.1), 0 2px 8px rgba(99,102,241,0.15)",
                  transform: "translateY(-1px)"
                },
              }}
            >
              <SearchIcon sx={{ 
                color: 'rgba(99,102,241,0.7)',
                fontSize: 16,
                mr: 1,
                flexShrink: 0
              }} />
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                flex: 1,
                gap: 0.5,
                overflowX: 'auto',
                overflowY: 'hidden',
                minWidth: 0,
                '&::-webkit-scrollbar': {
                  height: '3px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: 'rgba(99,102,241,0.3)',
                  borderRadius: '2px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: 'rgba(99,102,241,0.5)',
                },
                '& > div': {
                  display: 'flex !important',
                  flexWrap: 'nowrap !important',
                  gap: '4px',
                  overflow: 'visible !important',
                  maxHeight: 'none !important',
                  maxWidth: 'none !important'
                },
                '& .MuiChip-root': {
                  flexShrink: 0,
                  height: '22px',
                  '& .MuiChip-label': {
                    padding: '0 8px',
                    fontSize: '11px',
                    fontWeight: 500
                  },
                  '& .MuiChip-deleteIcon': {
                    fontSize: '14px'
                  }
                }
              }}>
                {searchState.tags.length > 0 && (
                  <ChipList 
                    list={searchState.tags} 
                    onDelete={(index) => {
                      const newTags = [...searchState.tags];
                      newTags.splice(index, 1);
                      dispatch({
                        type: "UPDATE_SEARCH",
                        payload: { 
                          query: searchState.query,
                          tags: newTags 
                        }
                      });
                    }} 
                  />
                )}
                <Input
                  placeholder={searchState.tags.length > 0 ? "Add more tags..." : "Search and explore with AI-powered insights..."}
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onKeyDown={handleSearchSubmit}
                  disableUnderline
                  sx={{
                    flex: 1,
                    minWidth: '120px',
                    '& input': {
                      padding: '4px 0',
                      fontSize: '13px',
                      fontWeight: 400,
                      color: '#1a202c',
                      '&::placeholder': {
                        color: 'rgba(99,102,241,0.6)',
                        fontWeight: 400
                      }
                    }
                  }}
                />
              </Box>
              {showExactSearch && exactSearchToggle}
            </Box>
          </Box>

          {/* Right section: Actions and Profile */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>

            {/* Mobile Search Icon */}
            <Tooltip title="Search" arrow>
              <IconButton
                color="inherit"
                onClick={() => setMobileSearchOpen(!mobileSearchOpen)}
                size="medium"
                sx={{
                  background: "rgba(255,255,255,0.9)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(226,232,240,0.6)",
                  borderRadius: "8px",
                  width: 32,
                  height: 32,
                  display: { xs: 'flex', sm: 'none' },
                  "&:hover": { 
                    background: "rgba(255,255,255,1)",
                    borderColor: "rgba(99,102,241,0.3)",
                    transform: "translateY(-1px)",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
                  },
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
                }}
              >
                <SearchIcon sx={{ fontSize: 16, color: 'rgba(26,32,44,0.8)' }} />
              </IconButton>
            </Tooltip>

            {/* Download Extension Button */}
            {!isExtensionInstalled && (
              <Button
                variant="contained"
                onClick={() => window.open(
                  "https://chromewebstore.google.com/detail/datarate/gdjiledfnpkdcjocdiicgcagklenhfdl",
                  "_blank"
                )}
                size="small"
                startIcon={<CloudDownloadIcon sx={{ fontSize: 14 }} />}
                sx={{
                  background: "linear-gradient(135deg, #00ca99 0%, #00a87a 100%)",
                  borderRadius: "8px",
                  textTransform: "none",
                  boxShadow: "0 1px 4px rgba(0,202,153,0.3)",
                  px: 2,
                  py: 0.5,
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "white",
                  border: "1px solid rgba(255,255,255,0.2)",
                  "&:hover": { 
                    background: "linear-gradient(135deg, #00b88a 0%, #00966b 100%)",
                    boxShadow: "0 2px 8px rgba(0,202,153,0.4)",
                    transform: "translateY(-1px)"
                  },
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  display: { xs: 'none', sm: 'flex' }
                }}
              >
                Download Extension
              </Button>
            )}

            {/* Upgrade Button */}
            {showUpgrade && (
              <Button
                variant="contained"
                onClick={() => navigate("/plans")}
                size="small"
                startIcon={<StarsIcon sx={{ fontSize: 14 }} />}
                sx={{
                  background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                  borderRadius: "8px",
                  textTransform: "none",
                  boxShadow: "0 1px 4px rgba(99,102,241,0.3)",
                  px: 2,
                  py: 0.5,
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "white",
                  border: "1px solid rgba(255,255,255,0.2)",
                  "&:hover": { 
                    background: "linear-gradient(135deg, #5855eb 0%, #7c3aed 100%)",
                    boxShadow: "0 2px 8px rgba(99,102,241,0.4)",
                    transform: "translateY(-1px)"
                  },
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  display: { xs: 'none', sm: 'flex' }
                }}
              >
                Upgrade
              </Button>
            )}

            {/* Notifications */}
            <Tooltip title="Notifications" arrow>
              <IconButton
                color="inherit"
                onClick={handleNotifMenuOpen}
                size="medium"
                sx={{
                  background: "rgba(255,255,255,0.9)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(226,232,240,0.6)",
                  borderRadius: "8px",
                  width: 32,
                  height: 32,
                  "&:hover": { 
                    background: "rgba(255,255,255,1)",
                    borderColor: "rgba(99,102,241,0.3)",
                    transform: "translateY(-1px)",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
                  },
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
                }}
              >
                <Badge 
                  badgeContent={unreadCount} 
                  color="error" 
                  overlap="circular"
                  sx={{
                    '& .MuiBadge-badge': {
                      background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                      boxShadow: "0 2px 4px rgba(239,68,68,0.3)"
                    }
                  }}
                >
                  <NotificationsIcon sx={{ fontSize: 16, color: 'rgba(26,32,44,0.8)' }} />
                </Badge>
              </IconButton>
            </Tooltip>


            {/* Profile section */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                borderRadius: "8px",
                background: "rgba(255,255,255,0.9)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(226,232,240,0.6)",
                "&:hover": { 
                  background: "rgba(255,255,255,1)",
                  borderColor: "rgba(99,102,241,0.3)",
                  transform: "translateY(-1px)",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
                },
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                px: 1,
                py: 0.5,
              }}
              onClick={handleProfileMenuOpen}
            >
              <Avatar
                alt={fullName}
                src={user?.profile_image_url || ""}
                sx={{
                  width: 28,
                  height: 28,
                  background: user?.profile_image_url ? 'inherit' : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  fontSize: 12,
                  fontWeight: 600,
                  border: "2px solid rgba(255,255,255,0.8)",
                  boxShadow: "0 1px 4px rgba(99,102,241,0.2)"
                }}
              >
                {!user?.profile_image_url && userInitials}
              </Avatar>

              {/* Always show plan near avatar (default "Free plan" when no data yet / after refresh) */}
              <Box sx={{ ml: 1, display: { xs: 'block', sm: 'block' }, minWidth: 0 }}>
                {!isMobile && (
                  <Typography variant="body2" sx={{ 
                    fontWeight: 600, 
                    lineHeight: 1.2,
                    color: '#1a202c',
                    fontSize: '12px'
                  }}>
                    {fullName}
                  </Typography>
                )}
                <Typography variant="caption" sx={{ 
                  lineHeight: 1,
                  color: 'rgba(99,102,241,0.7)',
                  fontSize: '10px',
                  fontWeight: 500,
                  display: 'block'
                }}>
                  {user?.plan_name ?? ""}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Search Area */}
      <Box
        sx={{
          position: 'fixed',
          top: 64,
          left: 0,
          right: 0,
          zIndex: (theme) => theme.zIndex.drawer,
          background: "linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.98) 100%)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          borderBottom: "1px solid rgba(226,232,240,0.6)",
          boxShadow: "0 2px 8px 0 rgba(0, 0, 0, 0.1)",
          display: { xs: mobileSearchOpen ? 'block' : 'none', sm: 'none' },
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          p: 2,
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            background: "rgba(255,255,255,0.9)",
            backdropFilter: "blur(20px)",
            borderRadius: "10px",
            border: "1px solid rgba(226,232,240,0.6)",
            boxShadow: "0 1px 4px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.1)",
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            minHeight: 40,
            py: searchState.tags.length > 0 ? 0.5 : 0.5,
            px: 1.5,
            "&:hover": {
              border: "1px solid rgba(99,102,241,0.3)",
              boxShadow: "0 2px 8px rgba(99,102,241,0.1), 0 1px 4px rgba(0,0,0,0.05)",
            },
            "&:focus-within": {
              border: "1px solid rgba(99,102,241,0.5)",
              boxShadow: "0 0 0 2px rgba(99,102,241,0.1), 0 2px 8px rgba(99,102,241,0.15)",
            },
          }}
        >
          <SearchIcon sx={{ 
            color: 'rgba(99,102,241,0.7)',
            fontSize: 16,
            mr: 1,
            flexShrink: 0
          }} />
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            flex: 1,
            gap: 0.5,
            overflowX: 'auto',
            overflowY: 'hidden',
            minWidth: 0,
            '&::-webkit-scrollbar': {
              height: '3px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(99,102,241,0.3)',
              borderRadius: '2px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: 'rgba(99,102,241,0.5)',
            },
            '& > div': {
              display: 'flex !important',
              flexWrap: 'nowrap !important',
              gap: '4px',
              overflow: 'visible !important',
              maxHeight: 'none !important',
              maxWidth: 'none !important'
            },
            '& .MuiChip-root': {
              flexShrink: 0,
              height: '22px',
              '& .MuiChip-label': {
                padding: '0 8px',
                fontSize: '11px',
                fontWeight: 500
              },
              '& .MuiChip-deleteIcon': {
                fontSize: '14px'
              }
            }
          }}>
            {searchState.tags.length > 0 && (
              <ChipList 
                list={searchState.tags} 
                onDelete={(index) => {
                  const newTags = [...searchState.tags];
                  newTags.splice(index, 1);
                  dispatch({
                    type: "UPDATE_SEARCH",
                    payload: { 
                      query: searchState.query,
                      tags: newTags 
                    }
                  });
                }} 
              />
            )}
            <Input
              placeholder={searchState.tags.length > 0 ? "Add more tags..." : "Search and explore with AI-powered insights..."}
              value={searchQuery}
              onChange={handleSearchChange}
              onKeyDown={handleSearchSubmit}
              autoFocus
              disableUnderline
              sx={{
                flex: 1,
                minWidth: '100px',
                '& input': {
                  padding: '4px 0',
                  fontSize: '14px',
                  fontWeight: 400,
                  color: '#1a202c',
                  '&::placeholder': {
                    color: 'rgba(99,102,241,0.6)',
                    fontWeight: 400
                  }
                }
              }}
            />
          </Box>
        </Box>
        {showExactSearch && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
            {exactSearchToggle}
          </Box>
        )}
      </Box>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        PaperProps={{
          elevation: 3,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.1))',
            mt: 1.5,
            minWidth: 200,
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem
          onClick={() => {
            navigate("/profile");
            setProfile(true);
            handleProfileMenuClose();
          }}
        >
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Profile" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            navigate("/invitations");
            handleProfileMenuClose();
          }}
        >
          <ListItemIcon>
            <MailIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Invitations" />
        </MenuItem>
        {authorized && (
          <MenuItem
            onClick={() => {
              navigate("/organization");
              handleProfileMenuClose();
            }}
          >
            <ListItemIcon>
              <BusinessIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Organization settings" />
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            navigate("/plans");
            handleProfileMenuClose();
          }}
        >
          <ListItemIcon>
            <StarsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Plans" />
        </MenuItem>
        {authorized && (
          <MenuItem
            onClick={() => {
              navigate("/settings");
              handleProfileMenuClose();
            }}
          >
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Settings" />
          </MenuItem>
        )}
        <MenuItem
          onClick={() =>
            window.open(
              "https://chromewebstore.google.com/detail/datarate/gdjiledfnpkdcjocdiicgcagklenhfdl",
              "_blank"
            )
          }
        >
          <ListItemIcon>
            <CloudDownloadIcon fontSize="small" onClick={() =>
              window.open(
                "https://chromewebstore.google.com/detail/datarate/gdjiledfnpkdcjocdiicgcagklenhfdl",
                "_blank"
              )
            } />
          </ListItemIcon>
          <ListItemText primary="Download Extension" />
        </MenuItem>
        <Divider />
        {authorized && (
          <MenuItem
            onClick={() => {
              navigate("/my-tickets");
              handleProfileMenuClose();
            }}
          >
            <ListItemIcon>
              <SupportIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Report / Support" />
          </MenuItem>
        )}
        {authorized ? (
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </MenuItem>
        ) : (
          <MenuItem
            onClick={() => {
              handleProfileMenuClose();
              // Store current path before redirecting to login
              localStorage.setItem('redirectAfterLogin', window.location.pathname);
              navigate("/login");
            }}
          >
            <ListItemIcon>
              <LoginIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Login" />
          </MenuItem>
        )}
      </Menu>

      {/* Notifications Menu */}
      <Menu
        anchorEl={notifAnchorEl}
        open={openNotif}
        onClose={handleNotifMenuClose}
        PaperProps={{
          elevation: 3,
          sx: {
            overflow: 'hidden',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.1))',
            mt: 1.5,
            width: 320,
            maxHeight: 400,
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ p: 2, pb: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Notifications
          </Typography>
        </Box>
        <Divider />
        <Box sx={{ 
          maxHeight: 'calc(400px - 80px)', // Subtract header height
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#555',
          },
        }}>
          {loading ? (
            <MenuItem>
              <Typography variant="body2">Loading notifications...</Typography>
            </MenuItem>
          ) : notifications.length > 0 ? (
            [...notifications]
              .sort((a, b) => {
                // New notifications go to the top
                const aIsNew = newNotifications.some(n => n.id === a.id);
                const bIsNew = newNotifications.some(n => n.id === b.id);
                if (aIsNew && !bIsNew) return -1;
                if (!aIsNew && bIsNew) return 1;
                // Then sort by date (newest first)
                return new Date(b.create_time) - new Date(a.create_time);
              })
              .map((notif) => (
                <MenuItem 
                  key={notif.id} 
                  onClick={() => {
                    if (notif.link_url) {
                      window.open(notif.link_url, '_blank');
                    }
                    handleNotifMenuClose();
                  }}
                  sx={{ 
                    py: 1.5,
                    backgroundColor: newNotifications.some(n => n.id === notif.id) 
                      ? 'rgba(25, 118, 210, 0.04)' 
                      : 'inherit',
                    cursor: notif.link_url ? 'pointer' : 'default'
                  }}
                >
                  <Box sx={{ width: '100%' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                      <Typography 
                        variant="body2" 
                        sx={{
                          flex: 1,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          lineHeight: 1.4,
                          maxHeight: '2.8em',
                          wordBreak: 'break-word',
                          whiteSpace: 'normal',
                          minHeight: '2.8em',
                        }}
                      >
                        {notif.message}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ 
                          whiteSpace: 'nowrap',
                          flexShrink: 0
                        }}
                      >
                        {formatNotificationDate(notif.create_time)}
                      </Typography>
                    </Box>
                  </Box>
                </MenuItem>
              ))
          ) : (
            <MenuItem onClick={handleNotifMenuClose}>
              <Typography variant="body2">No notifications</Typography>
            </MenuItem>
          )}
        </Box>
      </Menu>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: drawerWidth,
          },
        }}
      >
        <Toolbar />
        <Sidebar setProfile={setProfile} componentChange={componentChange} />
      </Drawer>
    </Box>
  );
}
